import { useEffect } from 'react';
import { SelectOption } from '../../types/ViewModel/SelectOption';
import { VerificationFilters } from '../../types/ViewModel/Verification/VerificationFilters';
import { QueryParamValueProps } from 'odfjell-query-manager';
import {
    hasQueryFilterParams,
    makeOptionsArray,
    makeOptionsStatusesArray,
    makeOptionsTypesArray,
    makeOptionsVesselsArray,
    getUsersAssignedReports,
} from './VerificationUtils';
import { stringifyOptionsArray } from '../../utils/verification/verificationUtils';

/**
 * Encapsulates the large verification filter initialization logic (previously a ~300 line useEffect in Verification.tsx).
 * Responsibilities:
 *  - Initialize default filters when no query parameters present.
 *  - Parse existing query parameters into filter state when present.
 *  - Derive and set user / manager / vessel selections.
 *  - Serialize selections back to query parameters when defaults are applied.
 */
export interface UseVerificationFilterInitializationParams {
    vesselList: any[] | undefined;
    populatedReportMetadataList: any[] | undefined;
    loggedInUser: any;
    queryParamUpdate: { queryParamValues: QueryParamValueProps[] } | null;
    verificationFilter: VerificationFilters | null;
    // Store values (initial sets)
    initialSelectedVessels: SelectOption[];
    initialSelectedManagers: SelectOption[];
    initialSelectedUsers: SelectOption[];
    // Store setters
    setVerificationFilter: (f: VerificationFilters) => void;
    setSelectedYears: (opts: SelectOption[]) => void;
    setSelectedUsers: (opts: SelectOption[]) => void;
    setSelectedManagers: (opts: SelectOption[]) => void;
    setSelectedVessels: (opts: SelectOption[]) => void;
    setSelectedReportTypes: (opts: SelectOption[]) => void;
    setSelectedReportStatuses: (opts: SelectOption[]) => void;
    setSelectedChecks: (opts: SelectOption[] | []) => void;
    setInitialSelectedVessels: (opts: SelectOption[]) => void;
    // Accept the original signature from useQueryParamsHandler (second arg typed union ClearOtherParamsProp)
    // Use 'clear-all' only if compatible, else default to undefined.
    triggerQueryParamsUpdate: (...args: any[]) => void;
    setDefaultFilters: (vf: VerificationFilters) => VerificationFilters;
}

export const useVerificationFilterInitialization = (params: UseVerificationFilterInitializationParams) => {
    const {
        vesselList,
        populatedReportMetadataList,
        loggedInUser,
        queryParamUpdate,
        verificationFilter,
        initialSelectedVessels,
        initialSelectedManagers,
        initialSelectedUsers,
        setVerificationFilter,
        setSelectedYears,
        setSelectedUsers,
        setSelectedManagers,
        setSelectedVessels,
        setSelectedReportTypes,
        setSelectedReportStatuses,
        setSelectedChecks,
        setInitialSelectedVessels,
        triggerQueryParamsUpdate,
        setDefaultFilters,
    } = params;

    useEffect(() => {
        if (vesselList?.length > 0 && populatedReportMetadataList?.length > 0 && loggedInUser && queryParamUpdate) {
            let _verificationFilters = verificationFilter;

            if (!_verificationFilters && !hasQueryFilterParams(queryParamUpdate.queryParamValues)) {
                _verificationFilters = {
                    selectedVessels: null,
                    selectedManagers: null,
                    selectedStatuses: null,
                    selectedTypes: null,
                    selectedUsers: null,
                    selectedYears: null,
                    selectedChecks: null,
                    initialSelectedVessels: null,
                    initialSelectedManagers: null,
                    initialSelectedStatuses: null,
                    initialSelectedTypes: null,
                    initialSelectedUsers: null,
                    initialSelectedYears: null,
                    availableChecks: null,
                };

                _verificationFilters = setDefaultFilters(_verificationFilters);
                // Ensure defaults are applied to the store immediately (not only via query params)
                if (_verificationFilters.selectedYears) {
                    setSelectedYears([..._verificationFilters.selectedYears]);
                }
                setVerificationFilter({ ..._verificationFilters });

                let _selectedOptionUsers: SelectOption[] = [];
                const _vesselList = vesselList;
                const _filteredVesselsInReports = _vesselList.filter((v) => populatedReportMetadataList.find((r) => r.imoNumber === v.imoNumber));
                const _filteredUsersAssignedReports: string[] = getUsersAssignedReports(_filteredVesselsInReports);

                const _filteredReportUserOptions = _filteredUsersAssignedReports.map((u) => {
                    return new SelectOption(u, u);
                });

                if (_verificationFilters.selectedUsers.find((v) => v.value === loggedInUser.fullName)) {
                    _selectedOptionUsers = [new SelectOption(loggedInUser.fullName, loggedInUser.fullName)];

                    const _userVessels: SelectOption[] = [];

                    _filteredVesselsInReports.forEach((r) => {
                        if (_selectedOptionUsers.find((u) => u.label === r.fleetPerformanceAlertResponsible)) {
                            _userVessels.push(new SelectOption(r.imoNumber.toString(), r.vesselName));
                        }
                    });

                    setInitialSelectedVessels([..._userVessels]);
                    _verificationFilters.selectedVessels = [..._userVessels];
                    _verificationFilters.initialSelectedVessels = [..._userVessels];
                    _verificationFilters.selectedUsers = _selectedOptionUsers;
                } else {
                    _verificationFilters.selectedUsers = _filteredReportUserOptions;
                }

                let usersStr = '';
                usersStr = _verificationFilters.selectedUsers?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedUsers) : 'none';

                let managersStr = '';
                managersStr =
                    _verificationFilters.selectedManagers?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedManagers) : 'none';

                let vesselsStr = '';
                vesselsStr = _verificationFilters.selectedVessels?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedVessels) : 'none';

                let yearsStr = '';
                yearsStr = _verificationFilters.selectedYears?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedYears) : 'none';

                let reportTypesStr = '';
                reportTypesStr = _verificationFilters.selectedTypes?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedTypes) : 'none';

                let reportStatusesStr = '';
                reportStatusesStr =
                    _verificationFilters.selectedStatuses?.length > 0 ? stringifyOptionsArray(_verificationFilters.selectedStatuses) : 'none';

                // checks default (all) handled after building from default filters, serialize after initial lists
                let checksStr = '';
                if (
                    _verificationFilters.selectedChecks == null ||
                    (_verificationFilters.availableChecks &&
                        _verificationFilters.selectedChecks?.length === _verificationFilters.availableChecks.length)
                ) {
                    checksStr = 'all';
                } else if (_verificationFilters.selectedChecks?.length === 0) {
                    checksStr = 'none';
                } else {
                    checksStr = stringifyOptionsArray(_verificationFilters.selectedChecks);
                }

                const queryParamValues: QueryParamValueProps[] = [
                    { name: 'users', newValue: usersStr },
                    { name: 'managers', newValue: managersStr },
                    { name: 'vessels', newValue: vesselsStr },
                    { name: 'reportTypes', newValue: reportTypesStr },
                    { name: 'reportStatuses', newValue: reportStatusesStr },
                    { name: 'years', newValue: yearsStr },
                    { name: 'checks', newValue: checksStr },
                ];

                triggerQueryParamsUpdate(queryParamValues, 'clear-all');
            } else {
                _verificationFilters = {
                    selectedVessels: null,
                    selectedManagers: null,
                    selectedStatuses: null,
                    selectedTypes: null,
                    selectedUsers: null,
                    selectedYears: null,
                    selectedChecks: null,
                    initialSelectedVessels: null,
                    initialSelectedManagers: null,
                    initialSelectedStatuses: null,
                    initialSelectedTypes: null,
                    initialSelectedUsers: null,
                    initialSelectedYears: null,
                    availableChecks: null,
                };

                _verificationFilters = setDefaultFilters(_verificationFilters);

                const presentParams = new Set(queryParamUpdate.queryParamValues.map((p) => p.name));
                queryParamUpdate.queryParamValues.forEach((param) => {
                    if (param.name === 'users') {
                        const users = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (users && users !== 'none') {
                            optionsArray = makeOptionsArray(users);
                        } else if (!users) {
                            optionsArray = [...initialSelectedUsers];
                        }
                        setSelectedUsers(optionsArray);
                        _verificationFilters.selectedUsers = optionsArray;
                    }

                    if (param.name === 'managers') {
                        const managers = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (managers && managers !== 'none') {
                            optionsArray = makeOptionsArray(managers);
                        } else if (!managers) {
                            optionsArray = [...initialSelectedManagers];
                        }
                        setSelectedManagers(optionsArray);
                        _verificationFilters.selectedManagers = optionsArray;
                    }

                    if (param.name === 'years') {
                        const years = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (years !== 'none') {
                            optionsArray = makeOptionsArray(years);
                        }
                        setSelectedYears(optionsArray);
                        _verificationFilters.selectedYears = optionsArray;
                    }

                    if (param.name === 'reportTypes') {
                        const reportTypes = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (reportTypes !== 'none') {
                            optionsArray = makeOptionsTypesArray(reportTypes);
                        }
                        setSelectedReportTypes(optionsArray);
                        _verificationFilters.selectedTypes = optionsArray;
                    }

                    if (param.name === 'reportStatuses') {
                        const reportStatuses = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (reportStatuses !== 'none') {
                            optionsArray = makeOptionsStatusesArray(reportStatuses);
                        }
                        setSelectedReportStatuses(optionsArray);
                        _verificationFilters.selectedStatuses = optionsArray;
                    }

                    if (param.name === 'vessels') {
                        const vessels = param.newValue;
                        let optionsArray: SelectOption[] = [];
                        if (vessels && vessels !== 'none') {
                            optionsArray = makeOptionsVesselsArray(vessels, vesselList);
                        } else if (!vessels) {
                            optionsArray = [...initialSelectedVessels];
                        }
                        setSelectedVessels(optionsArray);
                        _verificationFilters.selectedVessels = optionsArray;
                    }

                    if (param.name === 'checks') {
                        const checks = param.newValue;
                        if (checks === 'all') {
                            _verificationFilters.selectedChecks = null; // null => no filtering
                        } else if (checks === 'none') {
                            _verificationFilters.selectedChecks = [];
                            setSelectedChecks([]);
                        } else {
                            const optionsArray = makeOptionsArray(checks);
                            setSelectedChecks(optionsArray);
                            _verificationFilters.selectedChecks = optionsArray;
                        }
                    }
                });

                // Apply reset logic for filters whose params were cleared (absent means ALL)
                if (!presentParams.has('users') && initialSelectedUsers?.length) {
                    setSelectedUsers([...initialSelectedUsers]);
                    _verificationFilters.selectedUsers = [...initialSelectedUsers];
                }
                if (!presentParams.has('managers') && initialSelectedManagers?.length) {
                    setSelectedManagers([...initialSelectedManagers]);
                    _verificationFilters.selectedManagers = [...initialSelectedManagers];
                }
                const vesselsParamPresent = presentParams.has('vessels');
                if (!vesselsParamPresent) {
                    _verificationFilters.selectedVessels = null; // will be expanded to full user vessel list after derivation
                }

                const _selectedOptionUsers: SelectOption[] = _verificationFilters.selectedUsers;
                const _vesselList = vesselList;
                const _filteredVesselsInReports = _vesselList.filter((v) => populatedReportMetadataList.find((r) => r.imoNumber === v.imoNumber));

                const _userVessels: SelectOption[] = [];

                _filteredVesselsInReports.forEach((r) => {
                    if (_selectedOptionUsers.find((u) => u.label === r.fleetPerformanceAlertResponsible)) {
                        _userVessels.push(new SelectOption(r.imoNumber.toString(), r.vesselName));
                    }
                });

                setInitialSelectedVessels([..._userVessels]);
                _verificationFilters.initialSelectedVessels = [..._userVessels];
                if (_verificationFilters.selectedVessels === null) {
                    _verificationFilters.selectedVessels = [..._userVessels];
                    if (!vesselsParamPresent) {
                        setSelectedVessels([..._userVessels]);
                    }
                }

                setVerificationFilter({ ..._verificationFilters });
            }
        }
    }, [queryParamUpdate, vesselList, populatedReportMetadataList, loggedInUser]);
};
